import { lazy, Suspense, useCallback, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { tokenStore } from "@/lib/api";
import { MeProvider } from "@/lib/permissions";
import { Spinner } from "@/components/ui/spinner";
import { AppShell } from "@/components/app/AppShell";
import { CrumbProvider } from "@/components/app/crumbs";
import Login from "@/pages/Login";

// One chunk per section: the stats page should not wait on the broadcast editor.
const StatsPage = lazy(() => import("@/pages/stats/StatsPage"));
const ClientsList = lazy(() => import("@/pages/clients/ClientsList"));
const ClientPage = lazy(() => import("@/pages/clients/ClientPage"));
const AccountsList = lazy(() => import("@/pages/accounts/AccountsList"));
const AccountPage = lazy(() => import("@/pages/accounts/AccountPage"));
const CampaignsList = lazy(() => import("@/pages/campaigns/CampaignsList"));
const CampaignPage = lazy(() => import("@/pages/campaigns/CampaignPage"));
const BroadcastsPage = lazy(() => import("@/pages/broadcasts/BroadcastsPage"));
const AppRequestsPage = lazy(() => import("@/pages/requests/AppRequestsPage"));
const SourcesPage = lazy(() => import("@/pages/sources/SourcesPage"));
const SettingsPage = lazy(() => import("@/pages/settings/SettingsPage"));
const BotPage = lazy(() => import("@/pages/settings/BotPage"));
const PnlPage = lazy(() => import("@/pages/pnl/PnlPage"));
const WalletsPage = lazy(() => import("@/pages/wallets/WalletsPage"));
const AccessPage = lazy(() => import("@/pages/access/AccessPage"));
const LogsPage = lazy(() => import("@/pages/logs/LogsPage"));
const RequestAppPage = lazy(() => import("@/pages/miniapp/RequestAppPage"));

function Loading() {
  return (
    <div className="grid h-64 place-items-center">
      <Spinner className="text-muted-foreground" />
    </div>
  );
}

export default function App() {
  const [token, setToken] = useState(() => tokenStore.get());
  const { pathname } = useLocation();

  const logout = useCallback(() => {
    tokenStore.clear();
    setToken(null);
  }, []);

  // The Telegram mini app opens in a webview with no dashboard session, so it
  // sits outside both the auth gate and the shell. The path is the one the
  // bot's mini app is registered with in BotFather -- do not move it.
  if (pathname === "/dev/tg/request-app") {
    return (
      <Suspense fallback={<Loading />}>
        <RequestAppPage />
      </Suspense>
    );
  }

  if (!token) {
    return (
      <Routes>
        <Route
          path="/login"
          element={
            <Login
              onSuccess={(t) => {
                tokenStore.set(t);
                setToken(t);
              }}
            />
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <MeProvider>
      <CrumbProvider>
        <AppShell onLogout={logout}>
          <Suspense fallback={<Loading />}>
            {/* Tabbed pages take a trailing `/*`: each tab is its own URL. */}
            <Routes>
              <Route path="/*" element={<StatsPage />} />
              <Route path="/clients" element={<ClientsList />} />
              <Route path="/clients/:id/*" element={<ClientPage />} />
              <Route path="/accounts" element={<AccountsList />} />
              <Route path="/accounts/:id/*" element={<AccountPage />} />
              <Route path="/campaigns" element={<CampaignsList />} />
              <Route path="/campaigns/:id/*" element={<CampaignPage />} />
              <Route path="/broadcasts/*" element={<BroadcastsPage />} />
              <Route path="/app-requests" element={<AppRequestsPage />} />
              <Route path="/pnl" element={<PnlPage />} />
              <Route path="/wallets" element={<WalletsPage />} />
              <Route path="/sources/*" element={<SourcesPage />} />
              <Route path="/logs/*" element={<LogsPage />} />
              <Route path="/settings/bots/:provider" element={<BotPage />} />
              <Route path="/settings/*" element={<SettingsPage />} />
              <Route path="/access/*" element={<AccessPage />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AppShell>
      </CrumbProvider>
    </MeProvider>
  );
}
